import classNames from 'classnames';
import React from 'react';
import { FontIconBtn, Tooltip } from '@meitu/saas-ui';
import { TextAlignType, TextVerticalAlign, TextWritingType } from '@meitu/saas-editor-core/es/version-types';
import { useLocaleStore } from '@/i18n';
import LabelAction from '../label-action';
import './style.less';

type Props = {
    /**
     * 是否禁用
     */
    disabled?: boolean;
    /**
     * 文本方向
     */
    writingMode: TextWritingType;
    /**
     * 文本水平对齐方式
     */
    textAlign: TextAlignType;
    /**
     * 文本垂直对齐方式
     * 若不传入，则不开启垂直对齐相关控件
     */
    verticalAlign?: TextVerticalAlign;

    /**
     * 是否隐藏两端对齐
     */
    hideJustify?: boolean;

    onChange: (kind: 'textAlign' | 'verticalAlign', value: TextAlignType | TextVerticalAlign) => void;
};

export const TextAlignmentGroup: React.FC<Props> = ({
    disabled = false,
    writingMode,
    textAlign,
    verticalAlign,
    hideJustify = false,
    onChange,
}) => {
    const { $t } = useLocaleStore();

    const isHorizontal = writingMode === TextWritingType.HORIZONTAL_TB;

    function handleChange(kind: 'textAlign', value: TextAlignType): void;
    function handleChange(kind: 'verticalAlign', value: TextVerticalAlign): void;
    function handleChange(kind: 'textAlign' | 'verticalAlign', value: TextAlignType | TextVerticalAlign) {
        if (disabled) return;

        onChange(kind, value);
    }

    const TextAlignGroup = (
        <>
            {/* 左对齐，竖直文字时变为上对齐 */}
            <Tooltip
                placement="bottom"
                title={isHorizontal ? $t((v) => v.textAlignMent.alignLeft) : $t((v) => v.textAlignMent.alignTop)}
            >
                <div className="text-alignment-group__item">
                    <FontIconBtn
                        name={isHorizontal ? 'TextAlignmentLeftBold' : 'TextAlignmentTopBold'}
                        isActive={!disabled && textAlign === TextAlignType.LEFT}
                        fontSize={14}
                        type="button"
                        disable={disabled}
                        onClick={() => handleChange('textAlign', TextAlignType.LEFT)}
                    />
                </div>
            </Tooltip>

            {/* 居中对齐 */}
            <Tooltip placement="bottom" title={$t((v) => v.textAlignMent.alignCenter)}>
                <div className="text-alignment-group__item">
                    <FontIconBtn
                        name={isHorizontal ? 'TextAlignmentCenterBold' : 'TextAlignmentMiddleBold'}
                        isActive={!disabled && textAlign === TextAlignType.CENTER}
                        fontSize={14}
                        type="button"
                        disable={disabled}
                        onClick={() => handleChange('textAlign', TextAlignType.CENTER)}
                    />
                </div>
            </Tooltip>

            {/* 右对齐，竖直文字时变为下对齐 */}
            <Tooltip
                placement="bottom"
                title={isHorizontal ? $t((v) => v.textAlignMent.alignRight) : $t((v) => v.textAlignMent.alignBottom)}
            >
                <div className="text-alignment-group__item">
                    <FontIconBtn
                        name={isHorizontal ? 'TextAlignmentRightBold' : 'TextAlignmentBottomBold'}
                        isActive={!disabled && textAlign === TextAlignType.RIGHT}
                        fontSize={14}
                        type="button"
                        disable={disabled}
                        onClick={() => handleChange('textAlign', TextAlignType.RIGHT)}
                    />
                </div>
            </Tooltip>

            {/* 两边对齐 */}
            {!hideJustify ? (
                <Tooltip placement="bottom" title={$t((v) => v.textAlignMent.alignBetween)}>
                    <div
                        className={classNames('text-alignment-group__item', {
                            'text-alignment-group__item--rotate90': writingMode !== TextWritingType.HORIZONTAL_TB, // 由于暂时没有提供竖直文字的两端对齐的 icon，所以这里先临时处理
                        })}
                    >
                        <FontIconBtn
                            name="TextAlignmentJustifiedBold"
                            isActive={!disabled && textAlign === TextAlignType.JUSTIFY}
                            fontSize={14}
                            type="button"
                            disable={disabled}
                            onClick={() => handleChange('textAlign', TextAlignType.JUSTIFY)}
                        />
                    </div>
                </Tooltip>
            ) : null}
        </>
    );

    const VerticalAlignGroup = (
        <>
            {/* 上对齐，竖直文字时变为左对齐 */}
            <Tooltip
                placement="bottom"
                title={isHorizontal ? $t((v) => v.textAlignMent.alignTop) : $t((v) => v.textAlignMent.alignLeft)}
            >
                <div className="text-alignment-group__item">
                    <FontIconBtn
                        name={isHorizontal ? 'TextAlignmentTopBold' : 'TextAlignmentLeftBold'}
                        isActive={!disabled && verticalAlign === TextVerticalAlign.TOP}
                        fontSize={14}
                        type="button"
                        disable={disabled}
                        onClick={() => handleChange('verticalAlign', TextVerticalAlign.TOP)}
                    />
                </div>
            </Tooltip>

            {/* 竖直居中，竖直文字时变为居中对齐 */}
            <Tooltip
                placement="bottom"
                title={isHorizontal ? $t((v) => v.textAlignMent.alginVertical) : $t((v) => v.textAlignMent.alignCenter)}
            >
                <div className="text-alignment-group__item">
                    <FontIconBtn
                        name={isHorizontal ? 'TextAlignmentMiddleBold' : 'TextAlignmentCenterBold'}
                        isActive={!disabled && verticalAlign === TextVerticalAlign.CENTER}
                        fontSize={14}
                        type="button"
                        disable={disabled}
                        onClick={() => handleChange('verticalAlign', TextVerticalAlign.CENTER)}
                    />
                </div>
            </Tooltip>

            {/* 下对齐，竖直文字时变为右对齐 */}
            <Tooltip
                placement="bottom"
                title={isHorizontal ? $t((v) => v.textAlignMent.alignBottom) : $t((v) => v.textAlignMent.alignRight)}
            >
                <div className="text-alignment-group__item">
                    <FontIconBtn
                        name={isHorizontal ? 'TextAlignmentBottomBold' : 'TextAlignmentRightBold'}
                        isActive={!disabled && verticalAlign === TextVerticalAlign.BOTTOM}
                        fontSize={14}
                        type="button"
                        disable={disabled}
                        onClick={() => handleChange('verticalAlign', TextVerticalAlign.BOTTOM)}
                    />
                </div>
            </Tooltip>
        </>
    );

    return (
        <LabelAction title={$t((v) => v.textAlignMent.align)} align>
            <div className="text-alignment-group">
                {TextAlignGroup}
                {verticalAlign ? VerticalAlignGroup : null}
            </div>
        </LabelAction>
    );
};
